import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Flame, RefreshCw, Thermometer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultZone = {
  id: null,
  name: '',
  booking_room_id: '',
  ha_entity_id: '',
  comfort_temp: '20.5',
  eco_temp: '16.0',
  preheat_minutes: '60',
  postheat_minutes: '0',
  enabled: true,
};

const statusClass = {
  scheduled: 'bg-slate-100 text-slate-700',
  sent: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-amber-100 text-amber-700',
  skipped: 'bg-blue-100 text-blue-700',
};

const formatDate = (value) => {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' });
};

const HeatingPage = () => {
  const { user, hasCapability } = useAuth();
  const canManage = hasCapability('heating_admin');

  const [resources, setResources] = useState([]);
  const [zones, setZones] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingZone, setSavingZone] = useState(false);
  const [testingZoneId, setTestingZoneId] = useState(null);
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  const [zoneForm, setZoneForm] = useState(defaultZone);

  const resourceMap = useMemo(
    () => Object.fromEntries(resources.map((r) => [r.id, r.name])),
    [resources]
  );

  const fetchZones = useCallback(async () => {
    const response = await axios.get(`${API}/admin/heating/zones`);
    setZones(response.data || []);
  }, []);

  const fetchActions = useCallback(async (bookingId = '') => {
    const params = bookingId ? { bookingId } : {};
    const response = await axios.get(`${API}/admin/heating/actions`, { params });
    setActions(response.data || []);
  }, []);

  const fetchInitial = useCallback(async () => {
    try {
      const [resourcesRes] = await Promise.all([axios.get(`${API}/resources`), fetchZones(), fetchActions()]);
      setResources(resourcesRes.data || []);
    } catch (error) {
      toast.error('Kon heating data niet laden');
    } finally {
      setLoading(false);
    }
  }, [fetchActions, fetchZones]);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }
    fetchInitial();
  }, [canManage, fetchInitial]);

  const saveZone = async (e) => {
    e.preventDefault();
    if (!zoneForm.name || !zoneForm.booking_room_id || !zoneForm.ha_entity_id) {
      toast.error('Vul naam, ruimte en HA entity in');
      return;
    }

    try {
      setSavingZone(true);
      await axios.post(`${API}/admin/heating/zones`, {
        id: zoneForm.id || undefined,
        name: zoneForm.name,
        booking_room_id: zoneForm.booking_room_id,
        ha_entity_id: zoneForm.ha_entity_id,
        comfort_temp: parseFloat(zoneForm.comfort_temp),
        eco_temp: parseFloat(zoneForm.eco_temp),
        preheat_minutes: parseInt(zoneForm.preheat_minutes, 10),
        postheat_minutes: parseInt(zoneForm.postheat_minutes, 10),
        enabled: zoneForm.enabled,
      });
      toast.success(zoneForm.id ? 'Zone bijgewerkt' : 'Zone toegevoegd');
      setZoneForm(defaultZone);
      await fetchZones();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Zone opslaan mislukt');
    } finally {
      setSavingZone(false);
    }
  };

  const startEditZone = (zone) => {
    setZoneForm({
      id: zone.id,
      name: zone.name || '',
      booking_room_id: zone.booking_room_id || '',
      ha_entity_id: zone.ha_entity_id || '',
      comfort_temp: String(zone.comfort_temp ?? '20.5'),
      eco_temp: String(zone.eco_temp ?? '16.0'),
      preheat_minutes: String(zone.preheat_minutes ?? '60'),
      postheat_minutes: String(zone.postheat_minutes ?? '0'),
      enabled: !!zone.enabled,
    });
  };

  const runZoneTest = async (zoneId) => {
    try {
      setTestingZoneId(zoneId);
      await axios.post(`${API}/admin/heating/zones/${zoneId}/test-comfort`);
      toast.success('Comfort test gestart (2 min)');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Comfort test mislukt');
    } finally {
      setTestingZoneId(null);
    }
  };

  const applyBookingFilter = async (e) => {
    e.preventDefault();
    await fetchActions(bookingIdFilter.trim());
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <Card className="border-0 shadow-sm max-w-2xl">
          <CardHeader>
            <CardTitle>Heating</CardTitle>
            <CardDescription>Alleen beheerders en teamleiders hebben toegang.</CardDescription>
          </CardHeader>
        </Card>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-72 bg-slate-200 rounded-2xl"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="heating-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Heating</h1>
            <p className="text-slate-500">Automatische preheat en restore via Home Assistant</p>
          </div>
          <Button variant="outline" className="rounded-xl" onClick={fetchInitial}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Vernieuwen
          </Button>
        </div>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Zone configuratie</CardTitle>
            <CardDescription>Koppel een booking-ruimte aan een HA climate entity.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveZone} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Naam</Label>
                <Input value={zoneForm.name} onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>HA Entity ID</Label>
                <Input value={zoneForm.ha_entity_id} onChange={(e) => setZoneForm({ ...zoneForm, ha_entity_id: e.target.value })} placeholder="climate.serre_zaal" />
              </div>
              <div className="space-y-2">
                <Label>Ruimte</Label>
                <select
                  className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm"
                  value={zoneForm.booking_room_id}
                  onChange={(e) => setZoneForm({ ...zoneForm, booking_room_id: e.target.value })}
                >
                  <option value="">Selecteer ruimte</option>
                  {resources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Comfort temp (C)</Label>
                <Input type="number" step="0.1" value={zoneForm.comfort_temp} onChange={(e) => setZoneForm({ ...zoneForm, comfort_temp: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Eco temp (C)</Label>
                <Input type="number" step="0.1" value={zoneForm.eco_temp} onChange={(e) => setZoneForm({ ...zoneForm, eco_temp: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Preheat (minuten)</Label>
                <Input type="number" value={zoneForm.preheat_minutes} onChange={(e) => setZoneForm({ ...zoneForm, preheat_minutes: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Postheat (minuten)</Label>
                <Input type="number" value={zoneForm.postheat_minutes} onChange={(e) => setZoneForm({ ...zoneForm, postheat_minutes: e.target.value })} />
              </div>
              <div className="flex items-center gap-3 pt-7">
                <Switch checked={zoneForm.enabled} onCheckedChange={(checked) => setZoneForm({ ...zoneForm, enabled: checked })} />
                <span className="text-sm text-slate-700">Zone actief</span>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <Button type="submit" disabled={savingZone} className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                  {savingZone ? 'Opslaan...' : zoneForm.id ? 'Zone bijwerken' : 'Zone toevoegen'}
                </Button>
                {zoneForm.id && (
                  <Button type="button" variant="outline" className="rounded-xl" onClick={() => setZoneForm(defaultZone)}>
                    Annuleren
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Bestaande zones</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {zones.length === 0 && <p className="text-sm text-slate-500">Nog geen zones ingesteld.</p>}
            {zones.map((zone) => (
              <div key={zone.id} className="border border-slate-100 rounded-xl p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-900">{zone.name}</p>
                  <p className="text-sm text-slate-500">{resourceMap[zone.booking_room_id] || zone.booking_room_id} · {zone.ha_entity_id}</p>
                  <p className="text-xs text-slate-500 mt-1">Comfort {zone.comfort_temp}C · Eco {zone.eco_temp}C · Preheat {zone.preheat_minutes}m · Postheat {zone.postheat_minutes}m</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-xl" onClick={() => startEditZone(zone)}>Bewerken</Button>
                  <Button variant="outline" className="rounded-xl" onClick={() => runZoneTest(zone.id)} disabled={testingZoneId === zone.id}>
                    <Thermometer className="w-4 h-4 mr-2" />
                    {testingZoneId === zone.id ? 'Test...' : 'Test comfort'}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Heating acties (audit)</CardTitle>
            <CardDescription>Geplande en uitgevoerde acties voor preheat/restore.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={applyBookingFilter} className="flex flex-col sm:flex-row gap-3">
              <Input value={bookingIdFilter} onChange={(e) => setBookingIdFilter(e.target.value)} placeholder="Filter op booking ID (optioneel)" />
              <Button type="submit" className="rounded-xl">Filter</Button>
              <Button type="button" variant="outline" className="rounded-xl" onClick={() => { setBookingIdFilter(''); fetchActions(''); }}>Reset</Button>
            </form>

            <div className="space-y-2">
              {actions.length === 0 && <p className="text-sm text-slate-500">Geen actions gevonden.</p>}
              {actions.map((action) => (
                <div key={action.id} className="border border-slate-100 rounded-xl p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{action.type} · {action.booking_id}</p>
                    <p className="text-xs text-slate-500">Zone {action.zone_id} · gepland {formatDate(action.scheduled_at)} · uitgevoerd {formatDate(action.executed_at)}</p>
                    {action.last_error && <p className="text-xs text-red-600 mt-1">{action.last_error}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass[action.status] || statusClass.scheduled}`}>
                      {action.status}
                    </span>
                    <span className="text-xs text-slate-500">attempts: {action.attempts || 0}</span>
                    <Flame className="w-4 h-4 text-orange-500" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default HeatingPage;
